import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactGA from "react-ga4";
import { getSalatProduct } from "../../api";
import { api_url } from "../../constant";
import { useCart } from "../../store/cartProvider";
import ConvertToArabicNumbers from "../../utils/convert_numbers";

const Detail = ({ label, value }) => (
  <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow duration-200">
    <p className="text-lg font-semibold text-gray-700 mb-1">{label}:</p>
    <p className="text-gray-600 text-justify">{value}</p>
  </div>
);

function SalatProductDetails() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { id } = useParams();
  const cart = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  ReactGA.send({
    hitType: "pageview",
    page: window.location.pathname,
    title: "SalatMahsool Product Details",
  });

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setNotFound(false);
    getSalatProduct(id)
      .then((res) => {
        if (!isMounted) return;
        setProduct(res);
      })
      .catch((error) => {
        if (!isMounted) return;
        if (error.response?.status === 404) {
          setNotFound(true);
        } else {
          toast.error(t("salatMahsool.catalog.error"));
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [id, t]);

  if (loading) {
    return <div className="flex justify-center items-center h-screen text-xl">{t("common.loading")}</div>;
  }

  if (notFound || !product) {
    return (
      <div className="flex flex-col justify-center items-center h-[60vh] text-xl gap-4">
        <div>{t("salatMahsool.product.notFound")}</div>
        <Link to="/home/salatMahsool" className="text-primary underline">
          {t("salatMahsool.confirmation.backToCatalog")}
        </Link>
      </div>
    );
  }

  const images = (product.images || []).filter(Boolean);
  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));

  const price = parseFloat(product.price);
  const formattedPrice = isRtl ? ConvertToArabicNumbers(price.toFixed(2)) : price.toFixed(2);
  const hasLimitedQuantity = product.quantity != null;

  const handleQuantityChange = (e) => {
    let value = parseInt(e.target.value, 10);
    if (isNaN(value) || value < 1) value = 1;
    if (hasLimitedQuantity) value = Math.min(value, product.quantity);
    setQuantity(value);
  };

  const handleAddToCart = () => {
    cart.addItem(product.id, quantity, hasLimitedQuantity ? product.quantity : null);
    toast.success(t("salatMahsool.product.addedToCart"));
  };

  const name = i18n.language === "en" && product.name_en ? product.name_en : product.name;

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="mb-20 w-full py-10 px-4 lg:px-20">
      <div className="max-w-6xl mx-auto bg-white shadow-xl rounded-lg p-8 relative">
        <h1 className="text-4xl font-bold text-center mb-8 text-primary">{name}</h1>

        <div className="relative w-full h-[500px] overflow-hidden rounded-lg mb-8 border border-gray-200">
          {images.length > 0 ? (
            <>
              <img
                src={`${api_url}/${images[currentImageIndex]}`}
                alt={`${name} ${currentImageIndex + 1}`}
                className="w-full h-full object-contain bg-gray-100"
              />
              {images.length > 1 && (
                <>
                  <button
                    onClick={nextImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all duration-200"
                  >
                    &#10095;
                  </button>
                  <button
                    onClick={prevImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all duration-200"
                  >
                    &#10094;
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full text-sm">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full flex justify-center items-center bg-gray-100 text-gray-500 text-xl">
              {t("salatMahsool.product.noImages")}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Detail
            label={t("common.state")}
            value={`${formattedPrice} ${t("common.currency")}${product._unit ? ` / ${product._unit.name}` : ""}`}
          />
          {product._category && (
            <Detail
              label={t("salatMahsool.catalog.allCategories")}
              value={i18n.language === "en" && product._category.name_en ? product._category.name_en : product._category.name}
            />
          )}
          {product._state && (
            <Detail
              label={t("salatMahsool.product.state")}
              value={i18n.language === "en" && product._state.name_en ? product._state.name_en : product._state.name}
            />
          )}
          <Detail
            label={t("salatMahsool.product.available")}
            value={hasLimitedQuantity ? `${product.quantity} ${product._unit?.name || ""}` : t("salatMahsool.product.unlimited")}
          />
          <Detail label={t("salatMahsool.product.description")} value={product.description} />
        </div>

        <div className="flex flex-col items-center gap-4 mt-8">
          <div className="flex items-center gap-3">
            <label htmlFor="quantity" className="font-medium text-gray-700">
              {t("salatMahsool.product.quantity")}
            </label>
            <input
              type="number"
              id="quantity"
              min={1}
              max={hasLimitedQuantity ? product.quantity : undefined}
              value={quantity}
              onChange={handleQuantityChange}
              className="w-24 border border-gray-300 rounded-lg px-4 py-2 text-center"
            />
          </div>
          <button
            onClick={handleAddToCart}
            className="px-6 flex justify-center gap-3 items-center py-3 text-lg font-bold text-white transition-all duration-150 ease-linear bg-primary rounded-lg shadow-md hover:bg-primary-dark hover:shadow-lg focus:outline-none"
          >
            {t("salatMahsool.product.addToCart")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SalatProductDetails;
